import { CustomerInfo, Package, SubscriptionInfo } from '@revenuecat/purchases-js'
import { useContext, useEffect, useState } from 'react'
import { InternalMisconfigurationError } from '@/pages/external/ExternalPremiumLogin/ErrorComponents'
import { paymentSessionContext } from './ExternalPremiumLogin/lib'

import { updateSubscriptions } from '../user/userSlice'
import { useOauthSubscriptions, useStripePortalSession } from '@/services/auth'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { useErrorBoundary } from 'react-error-boundary'
import { CheckCircle, EmptyCircle } from '@/components/icons'
import { Button, ButtonPrimary } from '@/components/button'
import { t } from 'i18next'
import { Loading } from '@/components/loading'
import moment from 'moment'
import { userSelector } from '../user/selectors'
import { parsePeriod } from '@/pages/external/components/checkout/utils/pricing.utils'
import { authSelector } from '../auth/selectors'
import { DUPR_PLUS_RC_ENTITLEMENT } from '@/hooks/useHasFeature'
import { getEligiblePromotionsForUser } from '@/services/checkout'
import { trackPurchaseCompleted, type PurchaseFlow } from '@/config/trackingFunctions'

export const RevenueCatPurchase = ({
  isModal,
  isExternal,
  onPurchaseComplete,
}: {
  isModal?: boolean
  isExternal?: boolean
  onPurchaseComplete?: () => void
}) => {
  const { revenueCat } = useContext(paymentSessionContext)
  const [revCatPackages, setRevCatPackages] = useState([] as Package[])

  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | false>(false)
  const [isLoading, setIsLoading] = useState(true)

  const { subscriptions } = useAppSelector(userSelector)

  const { showBoundary } = useErrorBoundary()

  useEffect(() => {
    revenueCat
      .getCustomerInfo()
      .then(async (customerInfo) => {
        setCustomerInfo(customerInfo)
        const offerings = await revenueCat.getOfferings()

        if (isExternal) {
          setRevCatPackages(offerings.all['dupr_verified_no_trial']?.availablePackages)
          setIsLoading(false)
          return
        }

        // Authenticated subscription tab: honor a promotion's RC offering-id override
        // before falling back to the default offering. Mirrors the external/premium
        // sign-up flow (SubscriptionAwareProductFlow), which reads the same override
        // (rc_offering_id) off a promotion and resolves offerings.all[offeringId],
        // falling back to offerings.current when no override is present.
        const promotions = await getEligiblePromotionsForUser()
        const overrideOfferingId = promotions.find((p) => p.rcOfferingId)?.rcOfferingId ?? null
        const overrideOffering = overrideOfferingId ? (offerings.all[overrideOfferingId] ?? null) : null

        // Use the promotion's offering only when it actually has packages; otherwise
        // fall back to the default offering rather than showing nothing.
        const resolvedOffering =
          overrideOffering && overrideOffering.availablePackages.length > 0 ? overrideOffering : offerings.current

        if (resolvedOffering === null || resolvedOffering.availablePackages.length === 0) {
          // The manage/cancel view must stay reachable even when no offering
          // resolves (e.g. MiLP-purchased subscriptions have no matching RC
          // offering). Packages only decorate that view, so a missing offering
          // is fatal only when there is nothing to manage.
          if (hasManageableSubscription(customerInfo)) {
            setRevCatPackages([])
            setIsLoading(false)
            return
          }
          showBoundary(
            new InternalMisconfigurationError(
              resolvedOffering === null ? 'No offerings available for purchase' : 'No available packages for purchase'
            )
          )
          return
        }

        setRevCatPackages(resolvedOffering.availablePackages)
        setIsLoading(false)
      })
      .catch((error) => {
        console.error('Error fetching customer info:', error)
        showBoundary(new InternalMisconfigurationError('Failed to fetch customer information'))
      })
  }, [revenueCat, showBoundary, isLoading, subscriptions])

  // Purchase is blocked only when the user actually holds the DUPR+
  // entitlement. Keying this on "any active subscription record" is wrong: a
  // promotional or otherwise unrelated RC record (which can carry a far-future
  // expiry) would lock a non-DUPR+ user out of buying DUPR+ entirely.
  const hasDuprPlus = customerInfo !== false && customerInfo.entitlements.active[DUPR_PLUS_RC_ENTITLEMENT] != null
  const hasPackagesForSale = Boolean(revCatPackages && revCatPackages.length > 0)

  const content =
    customerInfo && revCatPackages ? (
      hasDuprPlus ? (
        <ShowWithSubscription customerInfo={customerInfo} offering={revCatPackages} />
      ) : (
        <>
          {hasPackagesForSale && (
            <ShowWithoutSubscription
              offering={revCatPackages}
              isModal={isModal}
              hasFreeTrial={!isExternal}
              purchaseFlow={isExternal ? 'partner' : 'dashboard'}
              onPurchaseComplete={onPurchaseComplete}
            />
          )}
          {/* Non-DUPR+ records (e.g. a promotional grant) and expired history
              stay visible below the package picker — on the full subscription
              page only, not in the upsell modal. */}
          {!isModal && (
            <ShowWithSubscription customerInfo={customerInfo} offering={revCatPackages} heading="Your memberships:" />
          )}
        </>
      )
    ) : (
      <Loading />
    )

  return isLoading ? (
    <Loading className={isModal ? 'mt-0' : 'mt-36'} />
  ) : (
    <div
      className={`${
        isModal ? 'text-navy-900' : 'text-white'
      } px-6 pt-4 pb-4 rounded-2xl flex flex-col gap-y-4 align-center`}
    >
      <h1 className="text-2xl">
        {!hasDuprPlus && hasPackagesForSale ? 'Purchase a DUPR+ Subscription' : 'Your DUPR+ Subscription'}
      </h1>

      <p className="flex items-center gap-x-2">
        <span className="text-2xl">
          <CheckCircle />
        </span>
        Annual Member Benefits
      </p>
      <p className="flex items-center gap-x-2">
        <span className="text-2xl">
          <CheckCircle />
        </span>
        Access to exclusive features
      </p>
      <p className="flex items-center gap-x-2">
        <span className="text-2xl">
          <CheckCircle />
        </span>
        Verified player badge
      </p>
      <p className="flex items-center gap-x-2">
        <span className="text-2xl">
          <CheckCircle />
        </span>
        Unlimited profile views
      </p>
      <p className="text-xs opacity-60">
        {hasDuprPlus || !hasPackagesForSale
          ? 'Your current membership:'
          : 'Select a package below to purchase a subscription. You will be redirected to the RevenueCat checkout flow.'}
      </p>
      {content}
    </div>
  )
}

/**
 * Whether the customer has a subscription worth showing the manage view for:
 * the DUPR+ entitlement, or any subscription record that has not yet expired.
 * Decides between rendering the manage view and hard-failing when no
 * RevenueCat offering resolves.
 */
function hasManageableSubscription(customerInfo: CustomerInfo): boolean {
  if (customerInfo.entitlements.active[DUPR_PLUS_RC_ENTITLEMENT] != null) return true
  return Object.values(customerInfo.subscriptionsByProductIdentifier).some(
    (details) => details?.expiresDate != null && new Date(details.expiresDate) > new Date()
  )
}

function ShowWithSubscription({
  customerInfo,
  offering,
  heading,
}: {
  customerInfo: CustomerInfo
  offering: Package[]
  heading?: string
}) {
  const [showHistory, setShowHistory] = useState(false)

  // Get all subscription IDs
  const allSubscriptionIds = Object.keys(customerInfo.subscriptionsByProductIdentifier)

  // Filter to only truly active subscriptions (expires_date in the future)
  const activeSubscriptionsList = allSubscriptionIds.filter((subscriptionId) => {
    const details = customerInfo.subscriptionsByProductIdentifier[subscriptionId]
    if (!details?.expiresDate) return false
    return new Date(details.expiresDate) > new Date()
  })

  // Get inactive/expired subscriptions
  const inactiveSubscriptionsList = allSubscriptionIds.filter((subscriptionId) => {
    const details = customerInfo.subscriptionsByProductIdentifier[subscriptionId]
    if (!details?.expiresDate) return true // No expiry date = inactive
    return new Date(details.expiresDate) <= new Date()
  })

  // Nothing to show (fresh account rendered below the package picker).
  if (allSubscriptionIds.length === 0) return null

  return (
    <div className="flex flex-col gap-y-4">
      {heading && <p className="text-xs opacity-60">{heading}</p>}
      {/* Active subscriptions */}
      {activeSubscriptionsList.map((subscription) => {
        const subscriptionDetails = customerInfo.subscriptionsByProductIdentifier[subscription]

        return (
          <SubscriptionEntry
            key={subscription}
            details={subscriptionDetails}
            packageItem={offering.find((p) => p.webBillingProduct.identifier === subscription)}
            managementURL={customerInfo.managementURL}
            isActive={true}
          />
        )
      })}

      {/* History accordion for inactive subscriptions */}
      {inactiveSubscriptionsList.length > 0 && (
        <div className="rounded-xl overflow-hidden bg-white shadow-lg">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full px-4 py-3 flex justify-between items-center text-navy-900 hover:bg-slate-50 transition-colors"
          >
            <span className="font-medium">Subscription History ({inactiveSubscriptionsList.length})</span>
            <span className={`transition-transform ${showHistory ? 'rotate-180' : ''}`}>▼</span>
          </button>
          {showHistory && (
            <div className="px-4 pb-4 flex flex-col gap-y-3 bg-slate-50">
              {inactiveSubscriptionsList.map((subscription) => {
                const subscriptionDetails = customerInfo.subscriptionsByProductIdentifier[subscription]

                return (
                  <SubscriptionEntry
                    key={subscription}
                    details={subscriptionDetails}
                    packageItem={offering.find((p) => p.webBillingProduct.identifier === subscription)}
                    managementURL={null}
                    isActive={false}
                  />
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ShowWithoutSubscription({
  offering,
  isModal,
  purchaseFlow,
  onPurchaseComplete,
}: {
  offering: Package[]
  isModal?: boolean
  hasFreeTrial?: boolean
  purchaseFlow: PurchaseFlow
  onPurchaseComplete?: () => void
}) {
  const { revenueCat, token } = useContext(paymentSessionContext)
  const [selectedPackage, setSelectedPackage] = useState<number>(0)
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useAppDispatch()
  const auth = useAppSelector(authSelector)
  const { mutateAsync: oauthSubscriptions } = useOauthSubscriptions()
  return isLoading ? (
    <Loading />
  ) : (
    <div className="flex flex-col gap-y-4">
      {offering.map((p, index) => {
        return (
          <RevenueCatPackage
            index={index}
            setSelection={setSelectedPackage}
            key={p.identifier}
            rcPackage={p}
            selected={selectedPackage === offering.indexOf(p)}
            isModal={isModal}
            hasTrial={false}
          />
        )
      })}
      <Button
        onClick={() => {
          setIsLoading(true)
          revenueCat
            .purchase({
              rcPackage: offering[selectedPackage],
            })
            .then(async () => {
              // First thing, so a failed subscription refresh below cannot lose the purchase row.
              trackPurchaseCompleted({ purchase_flow: purchaseFlow, product_id: offering[selectedPackage].identifier })
              // This makes the assumption that we update the subscription
              // on our backend when the purchase is successful.
              // This assumption has held true in testing.
              // const updateSubs = async () => {
              //   const res = await httpService.Post(activeSubscriptions, undefined)
              //   return res as SubscriptionResponse
              // }
              const newSubs = await oauthSubscriptions({ token: token ? token : auth.token! })
              dispatch(updateSubscriptions(newSubs.subscriptions))
              setIsLoading(false)
              onPurchaseComplete?.()
            })
            .catch(async () => {
              const newSubs = await oauthSubscriptions({ token: token ? token : auth.token! })
              dispatch(updateSubscriptions(newSubs.subscriptions))
              setIsLoading(false)

              // Why am I not using error boundary?
              // Because this isn't _reall_ an error.
              // The user could have just exited the purchase flow to check other packages
              // on offer.
            })
        }}
        className={`${isModal ? 'bg-primary text-white' : 'bg-white text-navy-900'} p-4 font-bold width-full`}
      >
        Start Now
      </Button>
    </div>
  )
}

type SubscriptionStore = 'stripe' | 'play_store' | 'app_store' | 'promotional' | 'amazon' | 'unknown'

function getStoreType(store: string | undefined): SubscriptionStore {
  if (!store) return 'unknown'
  const normalizedStore = store.toLowerCase()
  if (normalizedStore === 'stripe') return 'stripe'
  if (normalizedStore === 'play_store') return 'play_store'
  if (normalizedStore === 'app_store') return 'app_store'
  if (normalizedStore === 'promotional') return 'promotional'
  if (normalizedStore === 'amazon') return 'amazon'
  return 'unknown'
}

function getManagementLabel(storeType: SubscriptionStore): string {
  switch (storeType) {
    case 'stripe':
      return t('common.manageSubscription')
    case 'play_store':
      return 'Manage on Google Play'
    case 'app_store':
      return 'Manage on App Store'
    case 'promotional':
    case 'amazon':
    case 'unknown':
    default:
      return t('common.contactSupport')
  }
}

function getStoreDisplayName(storeType: SubscriptionStore): string {
  switch (storeType) {
    case 'stripe':
      return 'Stripe'
    case 'play_store':
      return 'Google Play'
    case 'app_store':
      return 'App Store'
    case 'promotional':
      return 'Promotional'
    case 'amazon':
      return 'Amazon'
    default:
      return 'Unknown'
  }
}

function SubscriptionEntry({
  details,
  packageItem,
  managementURL,
  isActive = true,
}: {
  details: SubscriptionInfo
  packageItem?: Package
  managementURL: string | null
  isActive?: boolean
}) {
  const { mutateAsync: getStripePortalSession, isLoading: isStripeLoading } = useStripePortalSession()
  const storeType = getStoreType(details.store)

  // Try to get a meaningful title - use package title, or format the product ID
  const getTitle = () => {
    if (packageItem?.webBillingProduct.title) {
      return packageItem.webBillingProduct.title
    }
    if (storeType === 'promotional') {
      return 'Promotional Subscription'
    }
    // Format product ID to be more readable (e.g., "prod_SjXOPHOEumKrA1" -> "DUPR+ Subscription")
    return 'DUPR+ Subscription'
  }

  const title = getTitle()

  // Get price info from package if available
  const priceInfo = packageItem?.webBillingProduct.currentPrice
  const periodInfo = packageItem?.webBillingProduct.normalPeriodDuration

  const handleManageSubscription = async () => {
    switch (storeType) {
      case 'stripe':
        try {
          const response = await getStripePortalSession()
          if (response.url) {
            // Same-tab navigation: mobile browsers block window.open after the
            // async gap breaks the user-gesture call stack (ENG-485). The Stripe
            // portal has its own return link.
            window.location.assign(response.url)
          }
        } catch (error) {
          console.error('Error fetching Stripe portal session:', error)
          window.location.assign('https://dupr.zendesk.com/hc/en-us/requests/new')
        }
        break
      case 'play_store':
        window.open('https://play.google.com/store/account/subscriptions', '_blank')
        break
      case 'app_store':
        window.open('https://apps.apple.com/account/subscriptions', '_blank')
        break
      case 'promotional':
      case 'amazon':
      case 'unknown':
      default:
        if (managementURL) {
          window.open(managementURL, '_blank')
        } else {
          window.open('https://dupr.zendesk.com/hc/en-us/requests/new', '_blank')
        }
        break
    }
  }

  const isLoading = storeType === 'stripe' && isStripeLoading
  const expiresDate = moment(details.expiresDate)
  const purchaseDate = moment(details.purchaseDate)

  // Determine status text for inactive subscriptions
  const getStatusText = () => {
    if (!isActive) return 'Expired'
    if (details.willRenew) return 'Auto-renewing'
    return 'Canceling'
  }

  return (
    <div className={`rounded-2xl shadow-lg overflow-hidden bg-white text-navy-900 ${!isActive ? 'opacity-75' : ''}`}>
      {/* Header with title and status */}
      <div className="px-4 py-3 bg-slate-50">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold">{title}</span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                isActive ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-500'
              }`}
            >
              {isActive ? 'Active' : 'Expired'}
            </span>
          </div>
          <CheckCircle className={`text-xl text-primary ${!isActive ? 'opacity-40' : ''}`} />
        </div>
      </div>

      {/* Details section */}
      <div className="px-4 py-3 space-y-3">
        {/* Price */}
        {priceInfo && periodInfo && (
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold">{priceInfo.formattedPrice}</span>
            <span className="text-sm text-slate-500">/ {parsePeriod(periodInfo).displayName}</span>
          </div>
        )}

        {/* Info grid */}
        <div className="grid grid-cols-2 gap-3 text-sm text-navy-900">
          <div>
            <div className="text-xs text-slate-400">
              {isActive ? (details.willRenew ? 'Renews' : 'Expires') : 'Expired'}
            </div>
            <div className="font-medium">{expiresDate.format('MMM D, YYYY')}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Started</div>
            <div className="font-medium">{purchaseDate.format('MMM D, YYYY')}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Billed via</div>
            <div className="font-medium">{getStoreDisplayName(storeType)}</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Status</div>
            <div className="font-medium">{getStatusText()}</div>
          </div>
        </div>

        {/* Promotional note */}
        {storeType === 'promotional' && (
          <div className="text-xs text-slate-500 bg-slate-100 p-2 rounded">
            This subscription was provided to you free of charge.
          </div>
        )}
      </div>

      {/* Action button - only show for active subscriptions */}
      {isActive && (
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
          <ButtonPrimary
            onClick={handleManageSubscription}
            title={isLoading ? 'Loading...' : `${getManagementLabel(storeType)} →`}
            disabled={isLoading}
            className="w-full"
          />
        </div>
      )}
    </div>
  )
}

const RevenueCatPackage = ({
  rcPackage,
  selected,
  setSelection,
  index,
  active,
  isModal,
  hasTrial,
}: {
  rcPackage: Package
  selected: boolean
  setSelection: (index: number) => void
  index: number
  active?: boolean | null
  isModal?: boolean
  hasTrial?: boolean
}) => {
  return (
    <div
      className={`${selected || active ? 'opacity-100' : 'opacity-70'}
      m-0 revenue-cat-package 
      flex
      justify-between
      items-center
      shadow-lg rounded-2xl ${isModal ? 'text-navy-900 bg-slate-50 p-4' : 'text-white'}
      ${active ? 'active cursor-default' : 'cursor-pointer '}
      `}
      onClick={() => {
        setSelection(index)
      }}
    >
      {active || (
        <>
          {hasTrial && (
            <div className="revenue-cat-badge-wrapper">
              <p className="revenue-cat-badge">First 7 Days free</p>
            </div>
          )}
        </>
      )}
      <div>
        <h2 className="text-md ">{rcPackage.webBillingProduct.title}</h2>
        <div className="flex items-end">
          <p className="text-lg font-normal font-semibold">
            {rcPackage.webBillingProduct.currentPrice.formattedPrice}/
          </p>
          <p className="text-xs mb-1">
            {(() => {
              const parsed = parsePeriod(rcPackage.webBillingProduct.normalPeriodDuration || 'P1M')
              return parsed.displayName
            })()}
          </p>
        </div>
      </div>
      <p className={`text-2xl`}>{selected || active ? <CheckCircle /> : <EmptyCircle />}</p>
    </div>
  )
}
